import { projectDisplayName } from '../organization/memberDirectory';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';
import {
  CollabDocsUIProvider,
  generateScopeAccentColor,
  useSharedDocumentTitles,
  type CollabDocsSession,
  type CollabSidebarCreateMenu,
} from '@nimbalyst/collab-bundle/docs-ui';
import type { TeamSummary } from '../api';
import { DecorativeIcon } from '../organization/DecorativeIcon';
import type { TeamJwt, TeamSession } from '../auth/teamJwt';
import { CollabLoadingPanel, PanelState } from '../components/Chrome';
import { useElementWidth, usePanelResize } from '../components/PanelResizer';
import { getProjectRouteId } from '../projectSelection';
import { orgFolderPath, orgTypePagePath, orgWikiPath } from '../routing';
import { BreadcrumbHeader, DocsBreadcrumbHeader } from '../pages/PagesBreadcrumbHeader';
import { PagesTrackersProvider, usePagesTrackers } from '../pages/PagesTrackers';
import { PagesTreePanel } from '../pages/PagesTreePanel';
import { NO_ACTIVE_PAGE, PagesActivePageProbe, type PagesActivePage } from '../pages/PagesActivePage';
import { usePagesSetType } from '../pages/usePagesSetType';
import { useDocsTree } from './docsTree';
import { useProjectDocsScope } from './useProjectDocsScope';
import { CollabDocumentStatusProvider } from './documentStatus';
import { pageHistoryDocumentId } from './webDocumentHistory';
import type { WebCollabDocumentsDataSource } from './WebCollabDocumentsDataSource';
import {
  webCollabHomePath,
  webCollabScopeKey,
} from './WebCollabHost';

type TeamProject = TeamSummary['projects'][number];

const DOCS_TREE_ID = 'browser-collab-tree';
const DOCS_TREE_DEFAULT_WIDTH = 264;
const DOCS_TREE_MIN_WIDTH = 180;
/**
 * What the docked tree has to leave for the content beside it. The content
 * is the point of this screen, so it outranks the tree: at 1024px the tree
 * cannot be dragged past 544px, and on a narrower shell it sits at its floor.
 */
const DOCS_TREE_MIN_CONTENT_WIDTH = 480;

/** The two ways the tree shows; see `docsTree.tsx`. */
export type DocsTreeMode = 'docked' | 'flyover';

/**
 * What a docs route needs from the shell it renders inside: the project it is
 * looking at, and how to move. Provided by `CollabDocsShell` rather than
 * threaded through the router outlet, because the list is the only navigation
 * this mode has -- there is no folder tree beside it -- and every route under
 * the shell needs the same handle on the project.
 */
export interface DocsShellContextValue {
  team: TeamSummary;
  project: TeamProject;
  projectId: string;
  navigate: (path: string) => void;
  onSelectProject: (project: TeamProject) => void;
}

const DocsShellContext = createContext<DocsShellContextValue | null>(null);

export function useDocsShell(): DocsShellContextValue {
  const value = useContext(DocsShellContext);
  if (!value) throw new Error('Docs routes render inside CollabDocsShell');
  return value;
}

export interface CollabDocsShellProps {
  team: TeamSummary;
  project: TeamProject;
  teamSession: TeamSession;
  getTeamJwt: (options?: { forceRefresh?: boolean }) => Promise<TeamJwt>;
  serverUrl: string;
  navigate: (path: string) => void;
  onSelectProject: (project: TeamProject) => void;
  createDataSource?: (options: ConstructorParameters<typeof WebCollabDocumentsDataSource>[0]) => WebCollabDocumentsDataSource;
  activeDocumentId?: string | null;
  activeFolderId?: string | null;
  /** The open typed page, by issue key or item id, as the route names it. */
  activeItemRef?: string | null;
  /** The open type page. */
  activeTypeId?: string | null;
  children: ReactNode;
}

/** The accent painted for this project: the switcher swatches, for one. */
export function projectAccentColor(team: TeamSummary, project: TeamProject): string {
  return generateScopeAccentColor(webCollabScopeKey(team.orgId, getProjectRouteId(project)));
}

/**
 * The project menu in a pane header. Exported because the Trackers surface
 * reuses this one rather than growing a second switcher that looks the same
 * until one of them changes.
 */
export function ProjectSwitcher({
  team,
  project,
  projects,
  onSelectProject,
}: {
  team: TeamSummary;
  project: TeamProject;
  projects: TeamProject[];
  onSelectProject: (project: TeamProject) => void;
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const menu = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
  });
  const interactions = useInteractions([
    useClick(menu.context),
    useDismiss(menu.context),
    useRole(menu.context, { role: 'menu' }),
    useListNavigation(menu.context, {
      listRef: itemRefs,
      activeIndex,
      onNavigate: setActiveIndex,
      loop: true,
    }),
  ]);
  const projectName = projectDisplayName(project);

  return (
    <>
      <button
        ref={menu.refs.setReference}
        {...interactions.getReferenceProps()}
        className="project-switcher-button -mx-[5px] -my-[3px] flex min-w-0 flex-1 cursor-pointer items-center gap-1 rounded-[5px] border-none bg-transparent px-[5px] py-[3px] text-left text-nim hover:bg-nim-hover aria-expanded:bg-nim-hover"
        type="button"
        aria-label="Switch project"
        aria-expanded={open}
      >
        <span className="min-w-0 truncate text-[0.9rem] font-bold">{projectName}</span>
        <DecorativeIcon icon="expand_more" size={16} className="shrink-0 text-nim-muted" />
      </button>
      {open && (
        <FloatingPortal>
          <FloatingFocusManager context={menu.context} modal={false}>
            <div
              ref={menu.refs.setFloating}
              style={menu.floatingStyles}
              {...interactions.getFloatingProps()}
              className="project-switcher-menu z-[100] min-w-[230px] rounded-[9px] border border-nim bg-nim p-1.5 text-nim shadow-[var(--console-shadow-lg)]"
            >
              {projects.map((candidate, index) => {
                const selected = candidate.projectId === project.projectId;
                return (
                  <button
                    className={`project-switcher-option flex w-full cursor-pointer items-center gap-2 rounded-[5px] border-none px-2 py-[7px] text-left text-nim hover:bg-nim-hover ${selected ? 'bg-nim-selected' : 'bg-transparent'}`}
                    key={candidate.projectId}
                    ref={(node) => { itemRefs.current[index] = node; }}
                    tabIndex={activeIndex === index ? 0 : -1}
                    type="button"
                    {...interactions.getItemProps({
                      onClick: () => {
                        setOpen(false);
                        if (!selected) onSelectProject(candidate);
                      },
                    })}
                  >
                    <span className="project-option-accent h-[26px] w-[3px] shrink-0 rounded-sm" style={{ backgroundColor: projectAccentColor(team, candidate) }} />
                    <span className="min-w-0 flex-1">
                      <strong className="block truncate text-[0.78rem]">{projectDisplayName(candidate)}</strong>
                      <small className="mt-px block truncate text-[0.66rem] capitalize text-nim-muted">{candidate.slug || candidate.projectId}</small>
                    </span>
                    {selected && <DecorativeIcon icon="check" size={17} className="text-nim-primary" />}
                  </button>
                );
              })}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}

/**
 * The pane sub-line from the mockup ("Stravu · 14 shared documents"). Rendered
 * as a node inside `CollabDocsUIProvider` so the count comes from the live
 * index rather than from a second API call.
 */
export function ProjectScopeSummary({ team }: { team: TeamSummary }) {
  const count = useSharedDocumentTitles().size;
  return <>{`${team.name} · ${count === 1 ? '1 shared document' : `${count} shared documents`}`}</>;
}

/**
 * The header strip, then the content -- the document list or the document.
 * By default nothing sits beside it: folders are rows in the list and crumbs
 * in the header, the way a file browser works, so the content is the full
 * width and a document is never shared with navigation.
 *
 * The tree comes back as `tree`, in one of two shapes chosen by `treeMode`:
 *
 * - `docked`: a grid column beside the content with a draggable seam, the
 *   pane the desktop app keeps open. It takes room; the content gives way.
 * - `flyover`: an absolute box that slides in from the left edge *over* the
 *   content, takes no room, and goes away once used.
 *
 * Below the phone breakpoint the docked pane is an overlay too (the phone
 * block in `styles.css`), since 430px cannot hold a tree beside a document.
 */
export function BrowserDocsLayout({
  header,
  children,
  state,
  tree,
  treeMode = 'flyover',
  treeHidden = false,
  onDismissTree,
}: {
  header: ReactNode;
  children: ReactNode;
  state?: 'loading' | 'error';
  /** The tree, when it is mounted. */
  tree?: ReactNode;
  treeMode?: DocsTreeMode;
  /** Mounted but put away: it takes no room and is out of the accessibility tree. */
  treeHidden?: boolean;
  /** Hides the tree; the scrim behind it and Escape inside it call this. */
  onDismissTree?: () => void;
}) {
  const treeRef = useRef<HTMLElement | null>(null);
  const [shell, setShell] = useState<HTMLElement | null>(null);
  // The shell is the region the docked tree and the content share, so it is
  // what the tree's width is clamped against -- never the window, which says
  // nothing about how much room this shell was actually given.
  const shellWidth = useElementWidth(shell);
  const treeResize = usePanelResize({
    storageKey: 'docs-tree',
    defaultWidth: DOCS_TREE_DEFAULT_WIDTH,
    edge: 'inline-end',
    label: 'Resize document tree',
    controlsId: DOCS_TREE_ID,
    containerWidth: shellWidth,
    min: DOCS_TREE_MIN_WIDTH,
    minComplement: DOCS_TREE_MIN_CONTENT_WIDTH,
  });
  const showing = Boolean(tree) && !treeHidden;
  const flyover = showing && treeMode === 'flyover';
  // Focus lands inside the fly-over when it opens, so Escape reaches it from
  // the gutter link that opened it. `preventScroll` because the document
  // behind it must not jump. A docked pane takes no focus: it is furniture.
  useEffect(() => {
    if (flyover) treeRef.current?.focus({ preventScroll: true });
  }, [flyover]);
  const treeElement = tree ? (
    <aside
      ref={treeRef}
      id={DOCS_TREE_ID}
      // Put away, the `hidden` class and not the attribute removes it: Tailwind
      // 3's `[hidden]` rule loses to the `flex` utility, which left the tree
      // stacked above the page in the one-column grid.
      className={
        treeHidden
          ? 'browser-collab-tree hidden'
          : flyover
            ? 'browser-collab-tree absolute inset-y-0 left-0 z-[60] flex w-[min(86%,320px)] min-w-0 flex-col overflow-hidden border-r border-nim bg-nim-secondary shadow-[var(--console-shadow-lg)] outline-none'
            : 'browser-collab-tree flex min-w-0 min-h-0 flex-col overflow-hidden border-r border-nim bg-nim-secondary outline-none'
      }
      aria-label="Page tree"
      hidden={treeHidden}
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || !flyover) return;
        event.stopPropagation();
        onDismissTree?.();
      }}
    >
      {/* Containment, not a scroller: the sidebar owns the scrolling region
          below its own pinned header. */}
      <div className="browser-collab-tree-body min-h-0 flex-1 overflow-hidden [&>*]:h-full [&>*]:min-h-0">{tree}</div>
    </aside>
  ) : null;
  return (
    <section
      ref={setShell}
      // The grid template is a real CSS rule keyed off `data-tree-mode` (it
      // reads the dragged width from the custom property) and lives beside
      // the splitter it moves.
      className={`browser-collab-docs-shell relative grid h-full min-h-0 w-full overflow-hidden bg-nim${state ? ' is-pending' : ''}`}
      data-personal-state="unavailable"
      data-shell-state={state}
      data-tree-mode={showing ? treeMode : 'hidden'}
      style={{ '--nim-docs-tree-width': `${treeResize.width}px` } as CSSProperties}
    >
      {/* Docked, the tree is the first grid column, so it comes first. */}
      {!flyover && treeElement}
      {/* A child of the shell rather than of the tree: the tree clips its
          overflow, and a handle straddling the seam has to hang over it. */}
      {showing && !flyover && <div className="browser-collab-tree-resizer" {...treeResize.separatorProps} />}
      <div className="browser-collab-content flex min-w-0 min-h-0 flex-col overflow-hidden">
        {header}
        <div className="browser-collab-content-scroll min-h-0 flex-1 overflow-y-auto [&>*]:min-h-full">{children}</div>
      </div>
      {/* Behind the fly-over at every width; behind the docked pane only on a
          phone, where that pane is an overlay too. The `hidden` utility is
          what the phone block in `styles.css` overrides. */}
      {showing && (
        <button
          type="button"
          className={`console-pane-scrim absolute inset-0 z-[55] border-none bg-[color-mix(in_srgb,black_30%,transparent)]${flyover ? '' : ' hidden'}`}
          aria-label="Dismiss page tree"
          onClick={onDismissTree}
        />
      )}
      {flyover && treeElement}
    </section>
  );
}

type ConnectedDocsLayoutProps = Pick<CollabDocsShellProps, 'team' | 'project' | 'teamSession' | 'getTeamJwt' | 'serverUrl'
  | 'activeDocumentId' | 'activeFolderId' | 'activeItemRef' | 'activeTypeId' | 'navigate' | 'onSelectProject' | 'children'> & { session: CollabDocsSession };

function ConnectedDocsLayout(props: ConnectedDocsLayoutProps) {
  const { team, project, teamSession, navigate, onSelectProject, session, children } = props;
  const projectId = getProjectRouteId(project);
  const shell = useMemo<DocsShellContextValue>(
    () => ({ team, project, projectId, navigate, onSelectProject }),
    [navigate, onSelectProject, project, projectId, team],
  );
  return (
    <CollabDocsUIProvider session={session}>
      <PagesTrackersProvider team={team} projectId={projectId} memberId={teamSession.memberId} navigate={navigate}>
        {/* Wraps the header and the outlet together: the mounted document is the
            only thing that knows its connectivity, and the breadcrumb that shows
            it is a sibling, never an ancestor. */}
        <CollabDocumentStatusProvider>
          <DocsShellContext.Provider value={shell}>
            <PagesFrame {...props} projectId={projectId}>{children}</PagesFrame>
          </DocsShellContext.Provider>
        </CollabDocumentStatusProvider>
      </PagesTrackersProvider>
    </CollabDocsUIProvider>
  );
}

/** The header, the tree and the route, inside the docs and tracker providers. */
function PagesFrame({
  team,
  project,
  projectId,
  teamSession,
  getTeamJwt,
  serverUrl,
  activeDocumentId,
  activeFolderId,
  activeItemRef,
  activeTypeId,
  navigate,
  onSelectProject,
  children,
}: ConnectedDocsLayoutProps & { projectId: string }) {
  const { available: trackersOpen } = usePagesTrackers();
  const [probedPage, setProbedPage] = useState<PagesActivePage>(NO_ACTIVE_PAGE);
  const activePage = trackersOpen ? probedPage : NO_ACTIVE_PAGE;
  const activeRoute = useMemo(() => ({ itemRef: activeItemRef, typeId: activeTypeId }), [activeItemRef, activeTypeId]);
  const homePath = webCollabHomePath(team, projectId);
  const { docked, setDocked, flyoverOpen, setFlyoverOpen } = useDocsTree();
  const treeMode: DocsTreeMode | null = docked ? 'docked' : flyoverOpen ? 'flyover' : null;
  const hideTree = useCallback(() => {
    if (docked) setDocked(false);
    else setFlyoverOpen(false);
  }, [docked, setDocked, setFlyoverOpen]);
  const [createMenu, setCreateMenu] = useState<CollabSidebarCreateMenu | null>(null);
  const setType = usePagesSetType({ team, projectId, teamSession, getTeamJwt: () => getTeamJwt(), serverUrl, navigate });
  return (
    <BrowserDocsLayout
      header={(
        <>
          <DocsBreadcrumbHeader
            team={team}
            project={project}
            projectId={projectId}
            documentId={activeDocumentId}
            folderId={activeFolderId}
            pagePath={activePage.path}
            pageHistoryDocumentId={pageHistoryDocumentId(activePage)}
            onOpenType={(typeId) => navigate(orgTypePagePath(team, projectId, typeId))}
            onShowHome={() => navigate(homePath)}
            onSelectFolder={(folderId) => navigate(orgFolderPath(team, projectId, folderId))}
            treeDocked={docked}
            onDockTree={() => setDocked(true)}
            onShowAllPages={() => navigate(orgWikiPath(team, projectId))}
            createMenu={createMenu}
          />
          {setType.element}
          {trackersOpen ? <PagesActivePageProbe route={activeRoute} onChange={setProbedPage} /> : null}
        </>
      )}
      tree={(
        <PagesTreePanel
          mode={treeMode}
          activeDocumentId={activeDocumentId}
          activeItemId={activePage.itemId}
          activeTypeId={activePage.typeId}
          scopeName={(afterPick) => (
            <ProjectSwitcher
              team={team}
              project={project}
              projects={team.projects}
              onSelectProject={(next) => {
                afterPick();
                onSelectProject(next);
              }}
            />
          )}
          scopePath={<ProjectScopeSummary team={team} />}
          onShowHome={() => navigate(homePath)}
          onClose={hideTree}
          onSetPageType={setType.onSetPageType}
          registerCreateMenu={setCreateMenu}
        />
      )}
      treeMode={treeMode ?? undefined}
      treeHidden={!treeMode}
      onDismissTree={hideTree}
    >
      {children}
    </BrowserDocsLayout>
  );
}

/** Mounts one project-owned Shared Docs session around the browser route surface. */
export function CollabDocsShell({
  team,
  project,
  teamSession,
  getTeamJwt,
  serverUrl,
  navigate,
  onSelectProject,
  createDataSource,
  activeDocumentId,
  activeFolderId,
  activeItemRef,
  activeTypeId,
  children,
}: CollabDocsShellProps) {
  // The session lifecycle lives in `useProjectDocsScope`, which the feedback
  // route also mounts. Everything this route used to do inline -- the tagged
  // state, the host memo, the callbacks ref -- moved there intact.
  const { session, error } = useProjectDocsScope({
    team,
    project,
    teamSession,
    getTeamJwt,
    serverUrl,
    navigate,
    createDataSource,
  });

  if (error || !session) {
    return (
      <BrowserDocsLayout
        state={error ? 'error' : 'loading'}
        header={(
          <BreadcrumbHeader
            crumbs={[
              { id: 'team', label: team.name },
              { id: 'project', label: projectDisplayName(project), current: true },
            ]}
          />
        )}
      >
        {error ? (
          <PanelState
            tone="error"
            className="docs-state-panel m-8 w-full self-center"
            role="alert"
            data-docs-state="service-unavailable"
          >
            <strong>Shared documents are unavailable</strong>
            <span>{error.message}</span>
          </PanelState>
        ) : (
          <CollabLoadingPanel
            title="Opening shared documents"
            detail="Connecting to your team’s live document index."
          />
        )}
      </BrowserDocsLayout>
    );
  }

  return (
    <ConnectedDocsLayout
      team={team}
      project={project}
      teamSession={teamSession}
      getTeamJwt={getTeamJwt}
      serverUrl={serverUrl}
      session={session}
      activeDocumentId={activeDocumentId}
      activeFolderId={activeFolderId}
      activeItemRef={activeItemRef}
      activeTypeId={activeTypeId}
      navigate={navigate}
      onSelectProject={onSelectProject}
    >
      {children}
    </ConnectedDocsLayout>
  );
}
